import { isNativeCapacitorApp } from '@/lib/pwa/isNativeCapacitorApp'

type CapacitorWindow = typeof window & {
  Capacitor?: {
    getPlatform?: () => string
  }
  WagamourNative?: {
    platform?: string
  }
}

function getCapacitorPlatform(): string | null {
  if (typeof window === 'undefined') return null
  return (window as CapacitorWindow).Capacitor?.getPlatform?.() ?? null
}

function hasAndroidPlatformSignal(): boolean {
  if (typeof window === 'undefined') return false

  const platform = getCapacitorPlatform()
  if (platform === 'android') return true

  if ((window as CapacitorWindow).WagamourNative?.platform === 'android') {
    return true
  }

  try {
    return (
      new URLSearchParams(window.location.search).get('platform') === 'android'
    )
  } catch {
    return false
  }
}

/**
 * True when running inside the Capacitor iOS native shell (not mobile Safari).
 * Requires {@link isNativeCapacitorApp} — Capacitor JS alone is not enough.
 * Non-android native Cap shells are treated as iOS (primary App Store target).
 */
export function isCapacitorIosApp(): boolean {
  if (!isNativeCapacitorApp()) return false
  return !hasAndroidPlatformSignal()
}

/**
 * True when running inside a Capacitor Android native shell.
 * Requires {@link isNativeCapacitorApp} — getPlatform/query alone are not enough.
 */
export function isCapacitorAndroidApp(): boolean {
  if (!isNativeCapacitorApp()) return false
  return hasAndroidPlatformSignal()
}

/**
 * Whether NextAuth callbackUrl should use /auth/complete?native=capacitor.
 * Ordinary browsers (including Cap JS present but not native) must return false.
 */
export function shouldUseCapacitorOAuthCallback(): boolean {
  return isNativeCapacitorApp() && !isCapacitorAndroidApp()
}

/** Preferred OAuth provider for the current runtime. */
export function getPrimaryOAuthProvider(): 'apple' | 'google' {
  return isCapacitorIosApp() ? 'apple' : 'google'
}
